// THE PRE-SPEND GATES — `/not-available`, and where `/property` and `/risk` went.
//
// `/not-available` re-asks `v2ResolveProperty` on render. That is the whole correction to the prototype: its gates were a
// client-side branch on a fixture boolean, so typing `#/checkout` after picking the Ohio property rendered "One appeal —
// $29.99" for a state PROppeal does not serve (WEB_PATHWAY_INVENTORY.md §4 "Worse" #1, walked). Here there is no state to
// walk past — the screen asks, and renders what came back.
//
// WEB-PARITY-2B: THE APP ASKS THE ADDRESS, THE UNIT AND THE KIND OF PROPERTY ON ONE SCREEN (AppealEntryFlow.swift:239-257,
// EN-2), and so does `/` now. `/property`, the page the type picker had to itself, has nothing left to ask, and sends the
// owner to `/`, where the picker is. `/risk`, the page the can-raise card had to itself, is where the app never puts it:
// the app shows the card inside its purchase sheet (BuyPacketsSheet.swift `backfireCard`) and the protest window inside
// its credit-confirm sheet (AEF:510) (RK-4), so both are on `/checkout` now, and `/risk` sends the owner on to the terms.
// Both routes stay, so a link or a back button that names them still lands somewhere that works.

import type { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { unitOrNull } from "../app/handIn";
import { useSession } from "../app/session";
import { useCall } from "../app/useCall";
import { CopyNote, NotRunNote } from "../components/copy";
import { Actions } from "../components/layout";
import { prePayTemplate, prePayText } from "../components/prePayCopy";
import { Refusal } from "../components/Refusal";
import { stateNameOf, whereNext } from "./nextStep";

/** Every screen here needs the resolution, so they share one call. It names the held appeal, or — holding none — mints
 *  one, through the session (WEB-APPEAL-ID). The session adds the owner's type pick to it (WEB-PARITY-1B, PR-5): this
 *  re-ask used to send none, and the server stores an absent type as null. */
function useResolution() {
  const session = useSession();
  return useCall(
    () => session.resolveAddress({ typedAddress: session.typedAddress, unit: unitOrNull(session.typedUnit) }),
    [session.resolveAddress, session.appealId, session.typedAddress, session.typedUnit, session.revision],
  );
}

/**
 * THE GATES RE-ASK WITH THE TYPED ADDRESS, SO THEY NEED ONE. The session holds it in memory only — the appeal id is the
 * one thing a reload keeps — so after a reload there is none, and an empty address is never sent. Nothing the server
 * offers reads the address phase back before a spend (`record` is behind C-1, and is a billable lookup), so the owner
 * starts at `/`, where the address they type resolves onto the appeal this tab still holds.
 */
export function NeedsTypedAddress({ children }: { children: ReactNode }) {
  const { typedAddress } = useSession();
  if (typedAddress === "") return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Pending() {
  return (
    <p className="spinner-line" role="status">
      Checking this address…
    </p>
  );
}

// ── /not-available ───────────────────────────────────────────────────────────────────────────────

/**
 * The app's "We're still finishing \(state)" sheet (AppealEntryFlow.swift:322-352), as a page: its title (NA-1), G-1's
 * sentence — the server's `resolve.g1.comingSoon`, which is the sheet's body word for word — and its one way out, "Try a
 * different address" (NA-4). The web-only paragraph about how a state is opened had no app source and is gone (NA-2).
 *
 * NOT BUILT: the sheet's "Check if we support your state" list (NA-3). The list of served states has one writer, the
 * server's G-1 (`resolve/stateGate.ts` `SERVED_STATES`), which no endpoint serves and this package must not copy; and the
 * list's own footer ("…you can still explore the app, but we'll ask you to confirm before spending a credit there") is
 * not true of either client. Recorded in docs/WEB_READINESS.md, F-WP2B-3.
 *
 * THE WAITLIST IS NOT BUILT EITHER. The prototype captured an email for a "tell me when Ohio opens" list; no endpoint
 * exists, and the published Privacy Policy names no such purpose (§6 row 8 — needs a Privacy version bump first).
 */
export function NotAvailable() {
  const state = useResolution();
  const navigate = useNavigate();

  if (state.status === "pending") return <Pending />;
  if (state.status === "failed") return <Refusal failure={state.failure} context="This address" />;

  const g1 = state.response.decisions["G-1"];
  if (!g1.ran) {
    return <NotRunNote decision={g1} what="Whether we serve this state" />;
  }
  if (g1.outcome !== "block") {
    // The gate is not closed, so this screen has nothing to say (NA-5: the app never shows "This address can go on."). Do
    // not sit on a refusal the server withdrew — that is the mirror of walking past one it made. The owner goes where
    // the answer sends them, or back to `/` when the address still needs them there.
    return <Navigate to={whereNext(state.response) ?? "/"} replace />;
  }

  const name = stateNameOf(g1.value.state);
  return (
    <section className="sheet-page">
      {name !== null && <h1 data-app-copy="notAvailable.title">{prePayTemplate("notAvailable.title", { state: name })}</h1>}
      <CopyNote copy={g1.copy} tone="stop" />
      <Actions>
        <button className="btn primary" type="button" data-app-copy="notAvailable.tryAnother" onClick={() => navigate("/")}>
          {prePayText("notAvailable.tryAnother")}
        </button>
      </Actions>
    </section>
  );
}

// ── /property and /risk ──────────────────────────────────────────────────────────────────────────

/** `/property`: the kind of property is asked on `/` now, beside the address, as the app asks it (EN-2). */
export function Property() {
  return <Navigate to="/" replace />;
}

/** `/risk`: the can-raise card is on `/checkout` now, inside the purchase, as the app shows it (RK-4). */
export function Risk() {
  return <Navigate to="/account" replace />;
}
